/**
 * Data Fields Panel Component
 *
 * Organized form with Patient, Guarantor, and Insurance sections.
 * Auto-populates from parsed widget columns on PDFMetadata (Layer 2).
 * Matches desktop data_fields.py layout exactly.
 *
 * Features:
 * - Patient Demographics
 * - Guarantor Information (with "Same as Patient" option)
 * - Primary Insurance
 * - Secondary Insurance
 */

import { useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Grid,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useSessionStore } from '../../store/sessionStore';
import { usePDFStore } from '../../store/pdfStore';
import { pdfsApi } from '../../api/pdfs';

// Reverse map: PascalCase form field → snake_case extraction key (for bbox highlighting)
const FIELD_TO_EXTRACTION_KEY: Record<string, string> = {
  FirstName: 'patient_name',
  MiddleName: 'patient_name',
  LastName: 'patient_name',
  Suffix: 'patient_name',
  DateOfBirth: 'patient_dob',
  Gender: 'patient_sex',
  MedicalRecordNumber: 'patient_mrn',
  AddressLine1: 'patient_address',
  City: 'patient_city_state_zip',
  State: 'patient_city_state_zip',
  ZipCode: 'patient_city_state_zip',
  HomePhone: 'patient_phone',
  GuarantorName: 'guarantor_name',
  GuarantorRelationship: 'guarantor_relation',
  PrimaryInsuranceCompany: 'primary_payor',
  PrimaryPlanName: 'primary_plan',
  PrimaryPolicyNumber: 'primary_subscriber_id',
  PrimaryGroupNumber: 'primary_group',
  PrimarySubscriberName: 'primary_subscriber_name',
  SecondaryInsuranceCompany: 'secondary_payor',
  SecondaryPlanName: 'secondary_plan',
  SecondaryPolicyNumber: 'secondary_subscriber_id',
  SecondaryGroupNumber: 'secondary_group',
  SecondarySubscriberName: 'secondary_subscriber_name',
};

// All widget field names that map to pdf_files columns
const WIDGET_FIELDS = [
  'FirstName', 'MiddleName', 'LastName', 'Suffix',
  'DateOfBirth', 'Gender', 'MedicalRecordNumber',
  'AddressLine1', 'City', 'State', 'ZipCode',
  'HomePhone', 'MobilePhone', 'WorkPhone', 'EmailAddress',
  'GuarantorName', 'GuarantorRelationship',
  'PrimaryInsuranceCompany', 'PrimaryPlanName',
  'PrimaryPolicyNumber', 'PrimaryGroupNumber', 'PrimarySubscriberName',
  'PrimarySubscriberDOB', 'PrimarySubscriberGender', 'PrimarySubscriberRelationship', 'PrimaryInsuranceType',
  'SecondaryInsuranceCompany', 'SecondaryPlanName',
  'SecondaryPolicyNumber', 'SecondaryGroupNumber', 'SecondarySubscriberName',
  'SecondarySubscriberDOB', 'SecondarySubscriberGender', 'SecondarySubscriberRelationship', 'SecondaryInsuranceType',
] as const;

export const DataFieldsPanel: React.FC = () => {
  const { currentPDF, extractionResults, setHighlightBbox, currentPDFIndex, totalPDFs, updateCurrentPDFFields } = usePDFStore();
  const { customerId } = useSessionStore();
  const { control, setValue, watch } = useForm();
  const prevPdfIdRef = useRef<string | null>(null);

  // Auto-populate form from parsed widget columns on currentPDF (Layer 2)
  useEffect(() => {
    if (!currentPDF) return;
    // Only repopulate when switching to a different PDF
    if (prevPdfIdRef.current === currentPDF.pdf_id) return;
    prevPdfIdRef.current = currentPDF.pdf_id;

    for (const field of WIDGET_FIELDS) {
      const value = (currentPDF as Record<string, any>)[field];
      setValue(field, value ?? '');
    }
    // Also set the sameAsPatient checkbox
    setValue('sameAsPatient', currentPDF.SameAsPatient ?? true);
  }, [currentPDF?.pdf_id, currentPDF, setValue]);

  // Handle auto-save on change (debounced)
  const watchedFields = watch();

  useEffect(() => {
    if (!currentPDF || !customerId) return;
    // Skip auto-save on initial load
    if (prevPdfIdRef.current !== currentPDF.pdf_id) return;

    const timer = setTimeout(() => {
      handleAutoSave();
    }, 1000); // 1s debounce

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFields]);

  const handleAutoSave = async () => {
    if (!currentPDF || !customerId) return;

    // Build dict of changed fields
    const changedFields: Record<string, string> = {};

    for (const field of WIDGET_FIELDS) {
      const formValue = watchedFields[field] ?? '';
      const dbValue = (currentPDF as Record<string, any>)[field] ?? '';
      if (formValue !== dbValue) {
        changedFields[field] = formValue;
      }
    }

    if (Object.keys(changedFields).length === 0) return;

    try {
      console.log('[DataFieldsPanel] Auto-saving parsed fields for:', currentPDF.pdf_id);
      await pdfsApi.saveParsedFields(currentPDF.pdf_id, customerId, changedFields);
      // Mirror edits into the Zustand store so PatientMatchPanel reads current values
      updateCurrentPDFFields(changedFields as any);
    } catch (error) {
      console.error('[DataFieldsPanel] Auto-save failed:', error);
    }
  };

  // Handle field hover for PDF source highlighting (uses Layer 1 bbox data)
  const handleFieldHover = (fieldName: string) => {
    const extractionKey = FIELD_TO_EXTRACTION_KEY[fieldName];
    if (!extractionKey) return;

    const fieldData = extractionResults[extractionKey];
    if (fieldData && fieldData.source_location) {
      setHighlightBbox(fieldData.source_location as [number, number, number, number]);
    }
  };

  const handleFieldLeave = () => {
    setHighlightBbox(null);
  };

  const sameAsPatient = watch('sameAsPatient', true);

  return (
    <Box sx={{ p: 3, overflow: 'auto', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Extracted Facesheet Data
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {currentPDF?.status === 'Uploaded' && (
            <Chip
              icon={<CircularProgress size={16} />}
              label="Processing..."
              color="warning"
              variant="outlined"
              size="small"
            />
          )}
          {currentPDF?.status === 'Parsed' && (
            <Chip
              label="Status: Parsed"
              color="success"
              variant="outlined"
              size="small"
            />
          )}
          <Chip
            label={currentPDF?.is_viewed ? 'Billable PDF' : 'Viewing...'}
            color="primary"
            size="small"
          />
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        PDF {currentPDFIndex + 1} of {totalPDFs || '?'}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* PATIENT SECTION */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Patient Information
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              name="FirstName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('FirstName')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              name="MiddleName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Middle Name"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('MiddleName')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              name="LastName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('LastName')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="DateOfBirth"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Date of Birth"
                  fullWidth
                  size="small"
                  placeholder="MM/DD/YYYY"
                  onMouseEnter={() => handleFieldHover('DateOfBirth')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="Gender"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Gender"
                  select
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('Gender')}
                  onMouseLeave={handleFieldLeave}
                >
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="MedicalRecordNumber"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Medical Record Number"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('MedicalRecordNumber')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name="AddressLine1"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Address Line 1"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('AddressLine1')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 5 }}>
            <Controller
              name="City"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="City"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('City')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 3 }}>
            <Controller
              name="State"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="State"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('State')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              name="ZipCode"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Zip Code"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('ZipCode')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              name="HomePhone"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Home Phone"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('HomePhone')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              name="MobilePhone"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="Mobile Phone" fullWidth size="small" />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              name="WorkPhone"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="Work Phone" fullWidth size="small" />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name="EmailAddress"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="Email Address" fullWidth size="small" type="email" />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* GUARANTOR SECTION */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Guarantor Information
        </Typography>

        <Controller
          name="sameAsPatient"
          control={control}
          defaultValue={true}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} />}
              label="Same as Patient"
            />
          )}
        />

        {!sameAsPatient && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <Controller
                name="GuarantorName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Guarantor Name"
                    fullWidth
                    size="small"
                    onMouseEnter={() => handleFieldHover('GuarantorName')}
                    onMouseLeave={handleFieldLeave}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="GuarantorRelationship"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} label="Relationship" select fullWidth size="small">
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="Self">Self</MenuItem>
                    <MenuItem value="Spouse">Spouse</MenuItem>
                    <MenuItem value="Parent">Parent</MenuItem>
                    <MenuItem value="Child">Child</MenuItem>
                    <MenuItem value="Sibling">Sibling</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* PRIMARY INSURANCE SECTION */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Primary Insurance
        </Typography>

        <Grid container spacing={2}>
          <Grid size={12}>
            <Controller
              name="PrimaryInsuranceCompany"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Insurance Company"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('PrimaryInsuranceCompany')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name="PrimaryPlanName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="Plan Name" fullWidth size="small" />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="PrimaryPolicyNumber"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Policy Number"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('PrimaryPolicyNumber')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="PrimaryGroupNumber"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Group Number"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('PrimaryGroupNumber')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name="PrimarySubscriberName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Subscriber Name"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('PrimarySubscriberName')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="PrimarySubscriberDOB"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Subscriber DOB"
                  fullWidth
                  size="small"
                  placeholder="MM/DD/YYYY"
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="PrimarySubscriberGender"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="Subscriber Gender" select fullWidth size="small">
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="PrimarySubscriberRelationship"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="Relationship to Insured" select fullWidth size="small">
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value="Self">Self</MenuItem>
                  <MenuItem value="Spouse">Spouse</MenuItem>
                  <MenuItem value="Parent">Parent</MenuItem>
                  <MenuItem value="Child">Child</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="PrimaryInsuranceType"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="Insurance Type" select fullWidth size="small">
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value="PPO">PPO</MenuItem>
                  <MenuItem value="HMO">HMO</MenuItem>
                  <MenuItem value="MEDICARE">Medicare</MenuItem>
                  <MenuItem value="MEDICAID">Medicaid</MenuItem>
                  <MenuItem value="INDEMNITY">Indemnity</MenuItem>
                  <MenuItem value="POS">POS</MenuItem>
                  <MenuItem value="EPO">EPO</MenuItem>
                </TextField>
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* SECONDARY INSURANCE SECTION */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Secondary Insurance
        </Typography>

        <Grid container spacing={2}>
          <Grid size={12}>
            <Controller
              name="SecondaryInsuranceCompany"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Insurance Company"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('SecondaryInsuranceCompany')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name="SecondaryPlanName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="Plan Name" fullWidth size="small" />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="SecondaryPolicyNumber"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Policy Number"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('SecondaryPolicyNumber')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="SecondaryGroupNumber"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Group Number"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('SecondaryGroupNumber')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name="SecondarySubscriberName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Subscriber Name"
                  fullWidth
                  size="small"
                  onMouseEnter={() => handleFieldHover('SecondarySubscriberName')}
                  onMouseLeave={handleFieldLeave}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="SecondarySubscriberDOB"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Subscriber DOB"
                  fullWidth
                  size="small"
                  placeholder="MM/DD/YYYY"
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="SecondarySubscriberGender"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="Subscriber Gender" select fullWidth size="small">
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="SecondarySubscriberRelationship"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="Relationship to Insured" select fullWidth size="small">
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value="Self">Self</MenuItem>
                  <MenuItem value="Spouse">Spouse</MenuItem>
                  <MenuItem value="Parent">Parent</MenuItem>
                  <MenuItem value="Child">Child</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="SecondaryInsuranceType"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="Insurance Type" select fullWidth size="small">
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value="PPO">PPO</MenuItem>
                  <MenuItem value="HMO">HMO</MenuItem>
                  <MenuItem value="MEDICARE">Medicare</MenuItem>
                  <MenuItem value="MEDICAID">Medicaid</MenuItem>
                  <MenuItem value="INDEMNITY">Indemnity</MenuItem>
                  <MenuItem value="POS">POS</MenuItem>
                  <MenuItem value="EPO">EPO</MenuItem>
                </TextField>
              )}
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
